import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, MessageCircle, X, Loader2, Send, Check } from "lucide-react";
import SuggestedActionChips from "./SuggestedActionChips";
import SaveSnapshotChip from "./SaveSnapshotChip";
import BuildPlanBadge from "./BuildPlanBadge";

const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

// 5s of silence before auto-submitting a dictation. Users asked for more
// breathing room than the 1.5s default. The "Stop & Submit" button on the
// listening bubble lets users override this timer at any time.
const VOICE_AUTOSUBMIT_MS = 5000;

function inferIntent(transcript) {
  if (!transcript) return null;
  const t = transcript.trim().toLowerCase();
  if (t.length < 4) return null;
  const verbs = [
    { re: /\b(add|put|place|install)\b\s+(a|an|the)?\s*(.+)/i, verb: "Add" },
    { re: /\b(remove|delete|take out|get rid of)\b\s+(a|an|the)?\s*(.+)/i, verb: "Remove" },
    { re: /\b(change|switch|replace|update)\b\s+(a|an|the)?\s*(.+)/i, verb: "Change" },
    { re: /\b(make|turn)\b\s+(a|an|the)?\s*(.+)/i, verb: "Modify" },
    { re: /\b(design|create|build|draft)\b\s+(a|an|the)?\s*(.+)/i, verb: "Design" },
    { re: /\b(move|shift|reposition)\b\s+(a|an|the)?\s*(.+)/i, verb: "Relocate" },
    { re: /\b(widen|enlarge|expand|grow)\b\s+(a|an|the)?\s*(.+)/i, verb: "Enlarge" },
    { re: /\b(shrink|reduce|narrow)\b\s+(a|an|the)?\s*(.+)/i, verb: "Reduce" },
  ];
  for (const { re, verb } of verbs) {
    const m = transcript.match(re);
    if (m && m[3]) {
      const subject = m[3].replace(/[.!?,;:]+$/, "").split(/\s+/).slice(0, 8).join(" ");
      return { verb, subject };
    }
  }
  return null;
}

/**
 * Floating microphone FAB + history modal for mobile.
 * Replaces the bottom drawer chat box on small screens. Layout:
 *   - Mic button: vertical-center right edge, ~16px from edge, 64px circle.
 *   - History pill: just below the mic, opens a fullscreen modal with chat log.
 * On voice activation:
 *   - Live transcript in the existing voice intent card (top-center) + here.
 *   - Auto-submits 1.5s after silence.
 *   - Stop icon while listening cancels without submit.
 */
export default function MobileMicFab({ messages, onSend, loading, suggestedActions = [], onClearSuggestions, buildQueue = [], snapshotChip }) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [intent, setIntent] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Typed-input fallback: keyboard-friendly path so users on iPhones or any
  // device without speech support can still send chat from mobile.
  const [typedText, setTypedText] = useState("");

  const recRef = useRef(null);
  const committedRef = useRef("");
  const silenceTimerRef = useRef(null);
  const pendingSubmitRef = useRef(false);
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;

  useEffect(() => {
    return () => {
      try { recRef.current?.stop?.(); } catch (err) { console.debug("[MobileMicFab] mic stop (unmount) failed:", err); }
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  // Auto-open history briefly when a new assistant message arrives
  const lastAssistantIdRef = useRef(null);
  useEffect(() => {
    const lastAssistant = [...(messages || [])].reverse().find((m) => m.role === "assistant");
    if (!lastAssistant) return;
    const key = lastAssistant.id || lastAssistant.content?.slice(0, 30);
    if (lastAssistantIdRef.current && lastAssistantIdRef.current !== key && !historyOpen) {
      // Don't auto-open if user is mid-recording.
      if (!listening) {
        setHistoryOpen(true);
        const t = setTimeout(() => setHistoryOpen(false), 6000);
        return () => clearTimeout(t);
      }
    }
    lastAssistantIdRef.current = key;
  }, [messages, listening, historyOpen]);

  const scheduleAutoSubmit = () => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      pendingSubmitRef.current = true;
      try { recRef.current?.stop?.(); } catch (err) { console.debug("[MobileMicFab] auto-stop failed:", err); }
    }, VOICE_AUTOSUBMIT_MS);
  };

  const startListening = () => {
    if (!SpeechRecognitionCtor) return;
    const rec = new SpeechRecognitionCtor();
    rec.lang = navigator.language || "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;
    committedRef.current = "";
    setInterim("");
    setIntent(null);
    pendingSubmitRef.current = false;

    rec.onresult = (event) => {
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          committedRef.current += r[0].transcript.trim() + " ";
        } else {
          interimText += r[0].transcript;
        }
      }
      setInterim(interimText);
      setIntent(inferIntent((committedRef.current + interimText).trim()));
      scheduleAutoSubmit();
    };
    rec.onerror = () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      setListening(false); setInterim(""); setIntent(null);
    };
    rec.onend = () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      setListening(false); setInterim(""); setIntent(null);
      if (pendingSubmitRef.current) {
        pendingSubmitRef.current = false;
        const final = (committedRef.current || "").trim();
        if (final && onSendRef.current) onSendRef.current(final);
      }
    };
    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
      scheduleAutoSubmit();
    } catch (err) {
      console.debug("[MobileMicFab] recognition.start failed:", err);
      setListening(false);
    }
  };

  const stopListening = () => {
    pendingSubmitRef.current = false;
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    try { recRef.current?.stop?.(); } catch (err) { console.debug("[MobileMicFab] manual mic stop failed:", err); }
    setListening(false); setInterim(""); setIntent(null);
  };

  // Manual override for the silence timer: stop dictation NOW and submit
  // whatever transcript we've captured. Wired to the orange "Stop & Submit"
  // button on the listening bubble.
  const submitNow = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    pendingSubmitRef.current = true;
    try { recRef.current?.stop?.(); } catch (err) { console.debug("[MobileMicFab] submit-now mic stop failed:", err); }
  };

  const handleTypedSubmit = (e) => {
    e?.preventDefault?.();
    const text = (typedText || "").trim();
    if (!text || loading) return;
    setTypedText("");
    onSendRef.current?.(text);
    // Retract iOS keyboard explicitly. Without this, iPhone Safari keeps
    // the keyboard mounted after the textarea is cleared, which obscures
    // the staged progress bar. Re-focus only happens on a manual tap.
    if (typeof document !== "undefined" && document.activeElement
        && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
  };

  const handleMicTap = () => {
    if (loading) return;
    if (listening) stopListening();
    else startListening();
  };

  const supported = !!SpeechRecognitionCtor;

  return (
    <>
      {/* Floating Mic FAB — vertically centered on the right edge */}
      <div
        className="lg:hidden fixed right-4 bottom-24 z-40 flex flex-col items-end gap-3"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {supported && (
          <button
            type="button"
            data-testid="mobile-mic-fab"
            onClick={handleMicTap}
            disabled={loading}
            aria-pressed={listening}
            aria-label={listening ? "Stop dictation" : "Start voice command"}
            className={`relative w-16 h-16 rounded-full grid place-items-center shadow-[0_10px_32px_rgba(0,0,0,0.7)] transition-all active:scale-95 ${
              listening
                ? "bg-orange-600 text-white border-2 border-orange-300"
                : loading
                ? "bg-zinc-800 text-zinc-500 border border-zinc-700"
                : "bg-brass-500 text-zinc-950 border-2 border-brass-300 hover:bg-brass-400"
            }`}
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : listening ? (
              <Square className="w-6 h-6 fill-current" />
            ) : (
              <Mic className="w-6 h-6" />
            )}
            {listening && (
              <span className="absolute inset-0 rounded-full border-2 border-orange-300 animate-ping pointer-events-none" />
            )}
          </button>
        )}

        <button
          type="button"
          data-testid="mobile-history-fab"
          onClick={() => setHistoryOpen(true)}
          aria-label="View chat history"
          className="relative w-12 h-12 rounded-full grid place-items-center bg-zinc-900/90 backdrop-blur border border-zinc-700 text-zinc-200 shadow-[0_8px_24px_rgba(0,0,0,0.5)] active:scale-95 transition-transform"
        >
          <MessageCircle className="w-5 h-5" />
          {messages?.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-orange-500 text-white text-[10px] font-mono grid place-items-center">
              {messages.length}
            </span>
          )}
        </button>

        {/* Mic hint label (only when idle + no messages) */}
        {!listening && (messages?.length || 0) === 0 && (
          <div className="bg-zinc-950/90 border border-zinc-800 backdrop-blur px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-brass-400 max-w-[140px] text-right">
            // Tap to speak
          </div>
        )}
      </div>

      {/* Live mic transcript bubble — bottom-center while listening */}
      {listening && (
        <div
          data-testid="mobile-mic-bubble"
          className="lg:hidden fixed bottom-6 left-4 right-4 z-50 bg-zinc-950/95 backdrop-blur-md border border-orange-500/60 px-4 py-3 shadow-[0_12px_48px_rgba(0,0,0,0.7)]"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-orange-300 mb-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
            </span>
            // Listening · auto-send 5s
          </div>
          <div className="font-body text-sm text-zinc-100 min-h-[1.5rem] mb-2">
            {intent ? (
              <>
                <span className="text-orange-400 font-mono text-[10px] uppercase tracking-widest mr-1.5">
                  {intent.verb}:
                </span>
                {intent.subject}
              </>
            ) : interim ? (
              <span className="text-zinc-300 italic">"{interim}"</span>
            ) : (
              <span className="text-zinc-500 italic">Speak now...</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="mobile-mic-cancel"
              onClick={stopListening}
              className="flex-1 inline-flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-zinc-500 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
            <button
              type="button"
              data-testid="mobile-mic-submit-now"
              onClick={submitNow}
              className="flex-[2] inline-flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              Stop & Submit
            </button>
          </div>
        </div>
      )}

      {/* History modal — fullscreen, with a sticky typed-input bar at the
          bottom so iPhone users always see the Send button above the
          keyboard. Uses 100dvh + safe-area-inset to stay clear of the home
          indicator and Safari URL bar. */}
      {historyOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-zinc-950/85 backdrop-blur-sm flex flex-col"
          data-testid="mobile-history-modal"
          style={{ height: "100dvh" }}
        >
          <header className="flex items-center justify-between px-4 h-14 border-b border-zinc-800 bg-zinc-950">
            <span className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
              // Conversation
            </span>
            <button
              type="button"
              onClick={() => setHistoryOpen(false)}
              className="p-2 text-zinc-300 hover:text-zinc-100"
              aria-label="Close history"
              data-testid="mobile-history-close"
            >
              <X className="w-5 h-5" />
            </button>
          </header>
          <div
            className="flex-1 overflow-y-auto px-4 py-5 space-y-5"
            style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
          >
            {(!messages || messages.length === 0) && (
              <div className="border border-dashed border-zinc-800 p-5 text-center">
                <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-2">
                  // No messages yet
                </div>
                <div className="font-body text-sm text-zinc-300">
                  Tap the brass microphone and describe what you want to design.
                </div>
              </div>
            )}
            {(messages || []).map((m, i) => (
              <div key={m.id || i} data-testid={`mobile-msg-${m.role}`}>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                  {m.role === "user" ? "// You" : "// Architect"}
                </div>
                <div
                  className={
                    m.role === "user"
                      ? "font-body text-sm text-zinc-100 border-l-2 border-orange-500 pl-3"
                      : "font-body text-sm text-zinc-200 border-l-2 border-brass-500 pl-3 leading-relaxed"
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="font-mono text-xs text-brass-400 border-l-2 border-brass-500 pl-3 architecting-dot">
                Architecting...
              </div>
            )}
          </div>
          {/* Type-fallback input — sticks to the bottom of the history sheet
              so iPhone Safari users can always reach a Send button. The
              `dvh`/safe-area-inset-bottom keeps it above the home indicator
              and out of the keyboard's path. */}
          <form
            onSubmit={handleTypedSubmit}
            className="border-t border-zinc-800 bg-zinc-950 flex flex-col"
            style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
            data-testid="mobile-type-form"
          >
            <SaveSnapshotChip
              visible={!!snapshotChip?.visible}
              saving={!!snapshotChip?.saving}
              saved={!!snapshotChip?.saved}
              onSave={snapshotChip?.onSave}
              onDismiss={snapshotChip?.onDismiss}
            />
            <SuggestedActionChips
              actions={suggestedActions}
              loading={loading}
              onPick={(prompt) => {
                setTypedText(prompt);
                onClearSuggestions?.();
              }}
              onDismiss={onClearSuggestions}
            />
            <BuildPlanBadge queue={buildQueue} isThinking={loading} />
            <div className="px-3 py-2.5 flex items-end gap-2">
              <textarea
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleTypedSubmit();
                }
              }}
              placeholder="Describe a change..."
              rows={1}
              data-testid="mobile-type-input"
              className="flex-1 resize-none bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-3 py-2.5 font-body text-sm text-zinc-100 max-h-32 min-h-[44px]"
              disabled={loading}
              autoComplete="off"
              autoCorrect="on"
              autoCapitalize="sentences"
              enterKeyHint="send"
            />
            <button
              type="submit"
              disabled={!typedText.trim() || loading}
              data-testid="mobile-type-send"
              aria-label="Send message"
              className="grid place-items-center w-11 h-11 bg-brass-500 text-zinc-950 hover:bg-brass-400 disabled:bg-zinc-800 disabled:text-zinc-600 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
